import React, { useMemo } from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import { cleanSecurityName } from '../lib/securityName';

const PALETTE = [
  '#818cf8', '#34d399', '#fbbf24', '#f87171', '#60a5fa',
  '#a78bfa', '#f472b6', '#facc15', '#4ade80', '#fb923c',
];

const fmtUSD = (v) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(Number(v) || 0);

export default function AllocationDonut({ allocation, dollars, holdings }) {
  const entries = Object.entries(allocation || {})
    .filter(([, pct]) => pct > 0.0001)
    .sort((a, b) => b[1] - a[1]);

  // Build a per-sector list of top positions for the tooltip. Sorts each
  // sector's holdings by institution_value desc and keeps the top 3.
  const topPositionsBySector = useMemo(() => {
    const bySector = new Map();
    for (const h of holdings || []) {
      const key = h.sector || 'Unclassified';
      const v = Number(h.institution_value);
      if (!Number.isFinite(v) || v <= 0) continue;
      const list = bySector.get(key) || [];
      list.push({
        ticker: h.ticker || '—',
        name: cleanSecurityName(h.security_name) || h.ticker || '—',
        value: v,
      });
      bySector.set(key, list);
    }
    for (const list of bySector.values()) {
      list.sort((a, b) => b.value - a.value);
    }
    return bySector;
  }, [holdings]);

  if (entries.length === 0) {
    return (
      <div className="h-64 rounded border border-neutral-800 bg-neutral-900 flex items-center justify-center">
        <p className="text-sm text-neutral-500">Allocation appears after first sync.</p>
      </div>
    );
  }

  const data = entries.map(([type, pct], i) => ({
    name: type,
    pct,
    dollars: Number(dollars?.[type] || 0),
    color: PALETTE[i % PALETTE.length],
  }));

  return (
    <div className="h-64 rounded border border-neutral-800 bg-neutral-900 px-3 py-2 flex">
      <div className="flex-1">
        <ResponsiveContainer>
          <PieChart>
            <Pie
              data={data}
              dataKey="pct"
              nameKey="name"
              innerRadius={50}
              outerRadius={80}
              stroke="#0a0a0a"
            >
              {data.map((d) => (
                <Cell key={d.name} fill={d.color} />
              ))}
            </Pie>
            <Tooltip
              content={<SectorTooltip topPositionsBySector={topPositionsBySector} />}
            />
          </PieChart>
        </ResponsiveContainer>
      </div>
      <div className="w-44 flex flex-col justify-center text-xs space-y-1.5 pl-2">
        {data.map((d) => (
          <div key={d.name} className="flex items-center gap-2">
            <span
              className="w-3 h-3 rounded-sm shrink-0"
              style={{ backgroundColor: d.color }}
            />
            <span className="text-neutral-300 truncate">{d.name}</span>
            <span className="text-neutral-500 ml-auto">{d.pct.toFixed(0)}%</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function SectorTooltip({ active, payload, topPositionsBySector }) {
  if (!active || !payload || payload.length === 0) return null;
  const slice = payload[0].payload;
  const top = (topPositionsBySector.get(slice.name) || []).slice(0, 3);

  return (
    <div className="rounded border border-neutral-700 bg-neutral-950 px-3 py-2 text-xs shadow-lg">
      <div className="flex items-center gap-2 mb-1">
        <span
          className="w-2.5 h-2.5 rounded-sm shrink-0"
          style={{ backgroundColor: slice.color }}
        />
        <span className="font-semibold text-neutral-100">{slice.name}</span>
      </div>
      <div className="text-neutral-200 tabular-nums">
        {fmtUSD(slice.dollars)}{' '}
        <span className="text-neutral-500">({slice.pct.toFixed(1)}%)</span>
      </div>
      {top.length > 0 && (
        <div className="mt-2 pt-2 border-t border-neutral-800 space-y-0.5">
          <div className="text-[10px] uppercase tracking-wide text-neutral-500 mb-1">
            Top positions
          </div>
          {top.map((p) => (
            <div key={p.ticker} className="flex items-center justify-between gap-3">
              <span className="text-neutral-300 truncate max-w-[180px]">
                <span className="font-mono text-neutral-200">{p.ticker}</span>{' '}
                <span className="text-neutral-500">{p.name}</span>
              </span>
              <span className="text-neutral-200 tabular-nums shrink-0">
                {fmtUSD(p.value)}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
